/**
 * Game Library types + folder tree utilities. Mirror of platform-2.0 so the
 * practice apps and the in-game app stay aligned without a generated client.
 * All practice apps share this — one backend, one contract.
 */

/** A single play sent to / received from the backend. */
export type LibraryPlay = {
  play_id?: string | number;
  play_data?: Record<string, unknown>;
  isDeleted?: boolean;
  [key: string]: unknown;
};

export type LibraryGame = {
  game_id?: number;
  game_description?: string | null;
  game_date?: string | null;
  complete?: boolean;
  folder_id?: number | null;
  /**
   * The day ("2026-11-04") its coach confirmed in Save Game. Offline-queued
   * saves only, set once it syncs (onKeepConfirmedDay); never sent to POST /games.
   */
  confirmed_day?: string;
  /** Custom library sort position (global flattened index; 0 = unordered). */
  position?: number | null;
  plays?: LibraryPlay[];
  maxpreps_settings?: unknown;
  /**
   * Optional idempotency key. When present, the backend returns the game
   * already created under this key instead of inserting a duplicate — makes
   * a save safe to retry (lost response / offline-queue flush).
   */
  idempotency_key?: string;
};

export type SavePlaysPayload = {
  game_id: number | string;
  plays: LibraryPlay[];
};

export type SaveSettingsPayload = Record<string, unknown>;

export type FolderDto = {
  folder_id: number;
  name: string;
  parent_id: number | null;
  position: number;
};

export type CreateFolderPayload = {
  name: string;
  parent_id?: number | null;
  position?: number;
};

export type UpdateFolderPayload = {
  name?: string;
  parent_id?: number | null;
  position?: number;
};

/** A folder rendered for the UI: its dto plus computed depth + child links. */
export type FolderTreeNode = {
  folder: FolderDto;
  depth: number;
  children: FolderTreeNode[];
};

/** Hard cap enforced by the API. Surfaced here so the UI can mirror the rule. */
export const MAX_FOLDER_DEPTH = 5;

/**
 * Build a tree from a flat folder list. Folders whose parent_id points at a
 * folder NOT in the list (deleted, foreign-org, etc.) are promoted to the
 * root rather than dropped — defensive against partial responses.
 */
export function buildFolderTree(folders: FolderDto[]): FolderTreeNode[] {
  const nodeById = new Map<number, FolderTreeNode>();
  for (const folder of folders) {
    nodeById.set(folder.folder_id, { folder, depth: 1, children: [] });
  }

  const roots: FolderTreeNode[] = [];
  for (const node of nodeById.values()) {
    const parentId = node.folder.parent_id;
    if (parentId != null && nodeById.has(parentId)) {
      nodeById.get(parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortChildren = (node: FolderTreeNode, depth: number) => {
    node.depth = depth;
    node.children.sort((a, b) => {
      const dp = a.folder.position - b.folder.position;
      return dp !== 0 ? dp : a.folder.folder_id - b.folder.folder_id;
    });
    node.children.forEach((c) => sortChildren(c, depth + 1));
  };
  roots.sort((a, b) => {
    const dp = a.folder.position - b.folder.position;
    return dp !== 0 ? dp : a.folder.folder_id - b.folder.folder_id;
  });
  roots.forEach((r) => sortChildren(r, 1));

  return roots;
}

export function collectFolderDescendantIds(
  folderId: number,
  folders: FolderDto[],
): number[] {
  const out: number[] = [folderId];
  const queue = [folderId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const f of folders) {
      if (f.parent_id === current) {
        out.push(f.folder_id);
        queue.push(f.folder_id);
      }
    }
  }
  return out;
}

export function depthOf(folderId: number | null, folders: FolderDto[]): number {
  if (folderId === null) return 0;
  const byId = new Map(folders.map((f) => [f.folder_id, f]));
  let depth = 0;
  let current: FolderDto | undefined = byId.get(folderId);
  while (current) {
    depth += 1;
    if (depth > MAX_FOLDER_DEPTH + 1) break;
    if (current.parent_id == null) break;
    current = byId.get(current.parent_id);
  }
  return depth;
}

export function subtreeHeightOf(folderId: number, folders: FolderDto[]): number {
  const childrenByParent = new Map<number, FolderDto[]>();
  for (const f of folders) {
    if (f.parent_id != null) {
      const list = childrenByParent.get(f.parent_id) ?? [];
      list.push(f);
      childrenByParent.set(f.parent_id, list);
    }
  }
  const visit = (id: number, depth: number): number => {
    if (depth > MAX_FOLDER_DEPTH + 1) return depth;
    const children = childrenByParent.get(id) ?? [];
    if (children.length === 0) return 1;
    let maxChild = 0;
    for (const c of children) {
      const h = visit(c.folder_id, depth + 1);
      if (h > maxChild) maxChild = h;
    }
    return 1 + maxChild;
  };
  return visit(folderId, 1);
}

export function disabledMoveDestinationIds(
  folderId: number,
  folders: FolderDto[],
): number[] {
  const cycleIds = collectFolderDescendantIds(folderId, folders);
  const cycleSet = new Set(cycleIds);
  const subtreeHeight = subtreeHeightOf(folderId, folders);
  const disabled: number[] = [...cycleIds];
  for (const f of folders) {
    if (cycleSet.has(f.folder_id)) continue;
    const d = depthOf(f.folder_id, folders);
    if (d + subtreeHeight > MAX_FOLDER_DEPTH) {
      disabled.push(f.folder_id);
    }
  }
  return disabled;
}
